import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Globe, Phone, User } from 'lucide-react';
import Logo from './Logo';
import { useApp, LANGUAGES, CURRENCIES } from '../context/AppContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from './ui/dropdown-menu';
import { openEnquiry } from './EnquiryModal';

const Header = () => {
  const { t, lang, setLang, currency, setCurrency, settings } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { label: t('nav_buy'), to: '/projects' },
    { label: t('nav_rent'), to: '/projects?status=rent' },
    { label: t('nav_offplan'), to: '/projects?status=offplan' },
    { label: t('nav_projects'), to: '/projects' },
    { label: t('nav_goldenvisa'), to: '/golden-visa' },
    { label: t('nav_developers'), to: '/projects' },
    { label: t('nav_areas'), to: '/areas' },
    { label: t('nav_about'), to: '/about' },
    { label: t('nav_contact'), to: '/contact' },
  ];

  const activeLang = LANGUAGES.find((l) => l.code === lang);
  // Transparent (white text) only over the homepage's dark hero; solid (dark text) everywhere else.
  const solid = scrolled || !isHome;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? 'bg-gobra-cream shadow-[0_2px_20px_-8px_rgba(47,61,76,0.3)] py-2' : 'bg-transparent py-3'}`}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
        <Link to="/" aria-label="GOBRA home">
          <Logo variant={solid ? 'dark' : 'light'} />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {navItems.map((item) => (
            <Link key={item.label} to={item.to}
              className={`text-sm font-body font-medium transition-colors hover:text-gobra-terracotta ${solid ? 'text-gobra-slate' : 'text-white/95'}`}>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Currency */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`hidden sm:flex items-center gap-1 text-sm font-medium px-2 py-1.5 rounded-md transition-colors ${solid ? 'text-gobra-slate hover:bg-gobra-sand' : 'text-white/95 hover:bg-white/10'}`}>
                {currency} <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{t('currency')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {Object.values(CURRENCIES).map((c) => (
                <DropdownMenuItem key={c.code} onClick={() => setCurrency(c.code)} className="cursor-pointer flex justify-between">
                  <span>{c.code}</span>
                  <span className="text-muted-foreground text-xs">{c.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-1.5 text-sm font-medium px-2 py-1.5 rounded-md transition-colors ${solid ? 'text-gobra-slate hover:bg-gobra-sand' : 'text-white/95 hover:bg-white/10'}`}>
                <Globe className="h-4 w-4" />
                <span className="hidden md:inline">{activeLang?.flag}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 max-h-[70vh] overflow-auto">
              <DropdownMenuLabel>{t('language')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGUAGES.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={`cursor-pointer gap-2 ${lang === l.code ? 'bg-gobra-sand font-semibold' : ''}`}>
                  <span>{l.flag}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button onClick={() => openEnquiry()} size="sm"
            className="hidden lg:inline-flex bg-gobra-green hover:bg-gobra-green-dark text-white rounded-full px-5">
            {t('list_property')}
          </Button>

          <button onClick={() => navigate('/login')}
            className={`hidden sm:flex items-center gap-1.5 text-sm font-medium ${solid ? 'text-gobra-slate' : 'text-white/95'}`}>
            <User className="h-4 w-4" /> {t('login')}
          </button>

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className={`xl:hidden p-1.5 rounded-md ${solid ? 'text-gobra-slate' : 'text-white'}`} aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm bg-gobra-cream overflow-auto">
              <SheetHeader>
                <SheetTitle className="text-left"><Logo variant="dark" /></SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col">
                {navItems.map((item) => (
                  <Link key={item.label} to={item.to} onClick={() => setMobileOpen(false)}
                    className="py-3 border-b border-gobra-sand text-gobra-slate font-medium text-base">
                    {item.label}
                  </Link>
                ))}
                <Button onClick={() => { setMobileOpen(false); openEnquiry(); }}
                  className="mt-6 bg-gobra-green hover:bg-gobra-green-dark text-white rounded-full">
                  {t('list_property')}
                </Button>
                <a href={`tel:${settings?.phone}`} className="mt-4 flex items-center gap-2 text-gobra-slate">
                  <Phone className="h-4 w-4 text-gobra-terracotta" /> {settings?.phone}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
